import { Component, ChangeDetectionStrategy, input, computed } from '@angular/core';
import { BaseChartDirective } from 'ng2-charts';
import { ChartOptions, ChartData } from 'chart.js';
import 'chartjs-adapter-date-fns';
import { AnnotationOptions } from 'chartjs-plugin-annotation';
import { ChartSeries, ChartPeriod, ChartReferenceLine } from '../interfaces/chart-data.interface';
import { buildReferenceLineAnnotations } from '../utils/chart-helpers';

@Component({
  selector: 'app-wbgt-line-chart',
  imports: [BaseChartDirective],
  templateUrl: './wbgt-line-chart.html',
  styleUrl: './wbgt-line-chart.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WbgtLineChart {
  seriesData = input.required<ChartSeries[]>();
  period = input<ChartPeriod>('1d');
  referenceLines = input<ChartReferenceLine[]>([]);

  // WBGTの5段階閾値と背景色 (日本生気象学会の指針に準拠した例)
  wbgtBoxes = input<Record<string, AnnotationOptions>>({
    dangerBox: {
      type: 'box',
      yMin: 31,
      backgroundColor: 'rgba(255, 99, 132, 0.1)',
      borderWidth: 0,
    },
    severeWarningBox: {
      type: 'box',
      yMin: 28,
      yMax: 31,
      backgroundColor: 'rgba(255, 159, 64, 0.1)',
      borderWidth: 0,
    },
    warningBox: {
      type: 'box',
      yMin: 25,
      yMax: 28,
      backgroundColor: 'rgba(255, 205, 86, 0.1)',
      borderWidth: 0,
    },
    cautionBox: {
      type: 'box',
      yMin: 21,
      yMax: 25,
      backgroundColor: 'rgba(75, 192, 192, 0.1)',
      borderWidth: 0,
    },
    safeBox: {
      type: 'box',
      yMax: 21,
      backgroundColor: 'rgba(54, 162, 235, 0.1)',
      borderWidth: 0,
    }
  });

  chartData = computed<ChartData<'line'>>(() => {
    const series = this.seriesData();
    if (series.length === 0) {
      return { datasets: [] };
    }

    const datasets = series.map(s => ({
      data: s.data.map(d => ({ x: d.timestamp.getTime(), y: d.value })),
      label: s.label,
      borderColor: s.color || '#9C27B0',
      backgroundColor: s.color || 'rgba(156, 39, 176, 0.4)',
      fill: true,
      tension: 0.1,
      pointRadius: 0,
    }));

    return { datasets };
  });

  chartOptions = computed<ChartOptions<'line'>>(() => {
    const annotations = { ...this.wbgtBoxes(), ...buildReferenceLineAnnotations(this.referenceLines()) };
    const currentPeriod = this.period();
    const tooltipFormat = currentPeriod === '1d' ? 'HH:mm' : 'MM/dd HH:mm';

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top',
        },
        annotation: {
          annotations
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        }
      },
      scales: {
        x: {
          type: 'time',
          time: {
            tooltipFormat,
            displayFormats: {
              minute: 'HH:mm',
              hour: 'HH:mm',
              day: 'MM/dd'
            }
          },
          display: true,
          grid: {
            display: false
          }
        },
        y: {
          display: true,
          beginAtZero: true,
          title: {
            display: true,
            text: 'WBGT (°C)'
          }
        }
      }
    };
  });
}
